import type {
  ListPlaybooksWithAnalyticsResponse,
  PlaybookUsageAnalyticsResponse,
} from '~/api/atlas/typedefs.autogen';
import type { LastModifiedBucket } from '~/features/playbooks/playbookLastModified';
import type {
  PlaybookDetailAnalytics,
  PlaybookInsights,
} from '~/features/playbooks/types/PlaybookInsights';
import type { PlaybookTableRow } from '~/features/playbooks/types/PlaybookTableRow';
import type { WithRtkOpts } from '~/redux/api/types';
import type {
  CreateGuidelineResponse,
  Guideline,
  GuidelineWithId,
  ListGuidelinesResponse,
  RiskLevel,
  UpdateGuidelineResponse,
} from '~/redux/api/types/playbooks/guidelines';
import type {
  ExportPlaybookArg,
  ExportPlaybookResponse,
  ValidateImportPlaybookArg,
  ValidateImportPlaybookResponse,
} from '~/redux/api/types/playbooks/playbookExportImport';
import type {
  CreatePlaybookRequest,
  CreatePlaybookResponse,
  GetPlaybookResponse,
  ListPlaybooksResponse,
  PlaybookStatus,
  UpdatePlaybookRequest,
  UpdatePlaybookResponse,
} from '~/redux/api/types/playbooks/playbooks';
import type {
  GetPlaybookTemplateArg,
  GetPlaybookTemplateResponse,
  ListPlaybookTemplatesResponse,
} from '~/redux/api/types/playbooks/playbookTemplates';

import { endpoints as autogenEnhancements } from '~/api/atlas/enhancements.autogen';
import { Tags } from '~/api/atlas/tags.autogen';

import type {
  QuestionGroupResponsePayload,
  QuestionGroupsResponsePayload,
  QuestionResponsePayload,
  QuestionsResponsePayload,
} from './questionGroupMapping';

import { mapAtlasError, mapAtlasErrorToCodedIfClientError } from './mapError';
import { mapPlaybookDetailAnalyticsResponse } from './playbookDetailAnalytics';
import {
  mapPlaybookInsightsParams,
  mapPlaybookInsightsResponse,
  type PlaybookInsightsArg,
} from './playbookInsights';
import {
  mapPlaybookListParams,
  mapPlaybookListResponse,
  mapPlaybookListSummaries,
  mapPlaybookListToLegacyData,
  type PlaybookListArg,
  type PlaybookListSummary,
} from './playbookList';
import {
  mapQuestionGroupResponse,
  mapQuestionResponse,
} from './questionGroupMapping';
import { atlasApiRoot } from './root';

// Apply the schema-derived providesTags / invalidatesTags for any generated
// endpoints that are not explicitly overridden below.
atlasApiRoot.enhanceEndpoints({ endpoints: autogenEnhancements });

const GUIDELINES_LIST_TAG = { type: Tags.Guidelines, id: 'LIST' } as const;
const PLAYBOOKS_LIST_TAG = { type: Tags.Playbooks, id: 'LIST' } as const;
// The generated `Tags` map has no separate tag for playbook templates (the
// schema groups templates under the same OpenAPI `Playbooks` tag) — reuse the
// `Playbooks` tag type with a distinguishing id, which is exactly as isolated
// for cache invalidation purposes as the legacy `TagType.PlaybookTemplates`.
const PLAYBOOK_TEMPLATES_LIST_TAG = {
  type: Tags.Playbooks,
  id: 'TEMPLATES_LIST',
} as const;
const QUESTIONS_LIST_TAG = { type: Tags.Saved_Questions, id: 'LIST' } as const;
const QUESTION_GROUPS_LIST_TAG = {
  type: Tags.Question_Groups,
  id: 'LIST',
} as const;

/**
 * Consumer-facing atlas endpoints. These override the raw generated endpoints
 * (`overrideExisting: true`) to reproduce the exact runtime behavior of the
 * legacy hand-written guidelines/playbooks/question-groups methods: the same
 * friendly argument and result shapes, the same request/response envelopes,
 * the same error mapping (`JsonApiError`, or `CodedError` for the playbook
 * mutations that used to convert 4xx JSON:API errors), and the same cache
 * tags/invalidation the deleted `src/redux/api/endpoints/{guidelines,
 * playbooks,questionGroups}.ts` enhancer files used to declare.
 *
 * `getPlaybookInsights` is declared as a `build.query` even though the
 * operation is `POST /playbooks/analytics` — the endpoint is a filtered read,
 * and the dashboards refetch it as filters change. The generated
 * `postPlaybookAnalytics` mutation is left in place but unused: as a mutation
 * it would need a manual trigger, and its schema-derived
 * `invalidatesTags: [Playbooks LIST]` would refetch the playbooks list on
 * every dashboard load. No cache tags are declared here — the three cards
 * sharing this query already dedupe on the `{ playbookIds, timeRange }` cache
 * key, and nothing invalidates analytics.
 *
 * `getPlaybookList` overrides the generated query to add the request params
 * the atlas route reads off `request.query_params` rather than declaring as
 * typed FastAPI deps — codegen can't see them, so the generated arg type is
 * `void`. `providesTags` is redeclared here because overriding an endpoint's
 * `query` replaces its whole definition, including the `LIST` tag the schema
 * annotation would otherwise attach — losing it would stop `createPlaybook` /
 * `updatePlaybook` / `deletePlaybook` from invalidating this list.
 *
 * Prefer `getPlaybookList` (table rows) or `getPlaybookListSummaries` (sparse
 * pickers / trees) for new call sites. `getPlaybooks` is a thin deprecated
 * adapter that preserves the old `{ data }` envelope for
 * `TicketReviewerPage.jsx` only — do not add new callers.
 */
export const atlasApi = atlasApiRoot.injectEndpoints({
  overrideExisting: true,
  endpoints: (build) => ({
    // ===== Guidelines =====

    getGuidelines: build.query<
      ListGuidelinesResponse,
      {
        lastModified?: LastModifiedBucket | null;
        limit?: number;
        offset?: number;
        risk?: RiskLevel | null;
        search?: string | null;
        sort?: string | null;
      }
    >({
      query: ({ lastModified, limit, offset, risk, search, sort } = {}) => {
        const params: Record<string, string | number> = {};
        // TODO move the component layer once pagination is implemented
        params['page[limit]'] = limit ?? 0;
        if (offset !== undefined) {
          params['page[offset]'] = offset;
        }
        if (search) {
          params.search = search;
        }
        if (sort) {
          params.sort = sort;
        }
        if (risk) {
          params.risk = risk;
        }
        if (lastModified) {
          params.last_modified = lastModified;
        }
        return { url: 'guidelines', params };
      },
      transformErrorResponse: mapAtlasError,
      providesTags: [GUIDELINES_LIST_TAG],
    }),

    getGuideline: build.query<
      { data: { attributes: GuidelineWithId } },
      string
    >({
      query: (guidelineId) => ({ url: `guidelines/${guidelineId}` }),
      transformErrorResponse: mapAtlasError,
      providesTags: (_result, _error, guidelineId) => [
        { type: Tags.Guidelines, id: guidelineId },
      ],
    }),

    createGuideline: build.mutation<GuidelineWithId, WithRtkOpts<Guideline>>({
      query: ({ rtkQueryOptions, ...data }) => ({
        url: 'guidelines',
        method: 'post',
        body: { data: { type: 'guideline', attributes: data } },
        rtkQueryOptions,
      }),
      transformResponse: (response: CreateGuidelineResponse) =>
        response.data.attributes,
      transformErrorResponse: mapAtlasError,
      invalidatesTags: [GUIDELINES_LIST_TAG],
    }),

    updateGuideline: build.mutation<
      GuidelineWithId,
      WithRtkOpts<GuidelineWithId> & {
        affectedPlaybookIds?: string[];
      }
    >({
      query: ({
        rtkQueryOptions,
        affectedPlaybookIds: _ignored,
        ...guideline
      }) => ({
        url: `guidelines/${guideline.uuid}`,
        method: 'patch',
        body: { data: { type: 'guideline', attributes: guideline } },
        rtkQueryOptions,
      }),
      transformResponse: (response: UpdateGuidelineResponse) =>
        response.data.attributes,
      transformErrorResponse: mapAtlasError,
      invalidatesTags: (_result, _error, arg) => [
        { type: Tags.Guidelines, id: arg.uuid },
        GUIDELINES_LIST_TAG,
        PLAYBOOKS_LIST_TAG,
        ...(arg.affectedPlaybookIds ?? []).map((playbookId) => ({
          type: Tags.Playbooks,
          id: playbookId,
        })),
      ],
    }),

    deleteGuideline: build.mutation<
      unknown,
      WithRtkOpts<{ guidelineId: string; affectedPlaybookIds?: string[] }>
    >({
      query: ({ guidelineId, rtkQueryOptions }) => ({
        url: `guidelines/${guidelineId}`,
        method: 'delete',
        rtkQueryOptions,
      }),
      transformErrorResponse: mapAtlasError,
      invalidatesTags: (_result, _error, arg) => [
        { type: Tags.Guidelines, id: arg.guidelineId },
        GUIDELINES_LIST_TAG,
        PLAYBOOKS_LIST_TAG,
        ...(arg.affectedPlaybookIds ?? []).map((playbookId) => ({
          type: Tags.Playbooks,
          id: playbookId,
        })),
      ],
    }),

    // ===== Playbooks =====
    // `getPlaybookInsights` is migrated below, under Playbook insights.

    /**
     * @deprecated Prefer `getPlaybookListSummaries`. Kept only for
     * `TicketReviewerPage.jsx` (existence check via legacy `{ data }` shape).
     */
    getPlaybooks: build.query<
      Pick<ListPlaybooksResponse, 'data'>,
      {
        search?: string | null;
        workflowId?: string | null;
        status?: PlaybookStatus | null;
      }
    >({
      query: ({ search, workflowId, status } = {}) => ({
        url: 'playbooks/list',
        params: mapPlaybookListParams({
          pageIndex: 1,
          pageSize: 0,
          search: search ?? '',
          workflowId,
          status,
          fields: ['name'],
        }),
      }),
      transformResponse: mapPlaybookListToLegacyData,
      transformErrorResponse: mapAtlasError,
      providesTags: [PLAYBOOKS_LIST_TAG],
    }),

    getPlaybook: build.query<GetPlaybookResponse, { playbookId: string }>({
      query: ({ playbookId }) => ({ url: `playbooks/${playbookId}` }),
      transformErrorResponse: mapAtlasError,
      providesTags: (_result, _error, arg) => [
        { type: Tags.Playbooks, id: arg.playbookId },
      ],
    }),

    // Deliberately not named `getPlaybookAnalyticsById`: reusing the generated
    // endpoint's name would make this override replace its whole definition,
    // dropping the `providesTags` the autogen enhancements attached to it.
    getPlaybookDetailAnalytics: build.query<
      PlaybookDetailAnalytics,
      { playbookId: string; pageIndex?: number; pageSize?: number }
    >({
      query: ({ playbookId, pageIndex, pageSize }) => {
        const params: Record<string, number> = {};
        if (pageIndex !== undefined && pageSize !== undefined) {
          params['most_negotiated[page][offset]'] = (pageIndex - 1) * pageSize;
          params['most_negotiated[page][limit]'] = pageSize;
        }
        return { url: `playbooks/${playbookId}/analytics`, params };
      },
      transformResponse: mapPlaybookDetailAnalyticsResponse,
      transformErrorResponse: mapAtlasError,
      // Scoped away from the playbook's own tag so saving a playbook doesn't
      // refetch analytics — editing a requirement can't change past reviews.
      providesTags: (_result, _error, arg) => [
        { type: Tags.Playbooks, id: `ANALYTICS_${arg.playbookId}` },
      ],
    }),

    createPlaybook: build.mutation<
      CreatePlaybookResponse,
      CreatePlaybookRequest
    >({
      query: (request) => ({
        url: 'playbooks',
        method: 'post',
        body: request,
      }),
      transformErrorResponse: mapAtlasErrorToCodedIfClientError,
      invalidatesTags: [PLAYBOOKS_LIST_TAG, GUIDELINES_LIST_TAG],
    }),

    updatePlaybook: build.mutation<
      UpdatePlaybookResponse,
      { playbookId: string; updatePlaybookRequest: UpdatePlaybookRequest }
    >({
      query: ({ playbookId, updatePlaybookRequest }) => ({
        url: `playbooks/${playbookId}`,
        method: 'patch',
        body: updatePlaybookRequest,
      }),
      transformErrorResponse: mapAtlasErrorToCodedIfClientError,
      invalidatesTags: (_result, _error, arg) => [
        { type: Tags.Playbooks, id: arg.playbookId },
        PLAYBOOKS_LIST_TAG,
        GUIDELINES_LIST_TAG,
      ],
    }),

    deletePlaybook: build.mutation<void, WithRtkOpts<{ playbookId: string }>>({
      query: ({ playbookId, rtkQueryOptions }) => ({
        url: `playbooks/${playbookId}`,
        method: 'delete',
        rtkQueryOptions,
      }),
      transformErrorResponse: mapAtlasError,
      invalidatesTags: [PLAYBOOKS_LIST_TAG, GUIDELINES_LIST_TAG],
    }),

    getPlaybookTemplates: build.query<ListPlaybookTemplatesResponse, void>({
      query: () => ({ url: 'playbooks/templates' }),
      transformErrorResponse: mapAtlasError,
      providesTags: [PLAYBOOK_TEMPLATES_LIST_TAG],
    }),

    getPlaybookTemplate: build.query<
      GetPlaybookTemplateResponse,
      GetPlaybookTemplateArg
    >({
      query: ({ templateId }) => ({
        url: `playbooks/templates/${templateId}`,
      }),
      transformErrorResponse: mapAtlasError,
      providesTags: (_result, _error, arg) => [
        { type: Tags.Playbooks, id: `TEMPLATE_${arg.templateId}` },
      ],
    }),

    exportPlaybook: build.mutation<ExportPlaybookResponse, ExportPlaybookArg>({
      query: ({ playbookId }) => ({
        url: `playbooks/${playbookId}/export`,
        method: 'post',
      }),
      transformErrorResponse: mapAtlasError,
      invalidatesTags: (_result, _error, arg) => [
        { type: Tags.Playbooks, id: arg.playbookId },
      ],
    }),

    validateImportPlaybook: build.mutation<
      ValidateImportPlaybookResponse,
      ValidateImportPlaybookArg
    >({
      query: ({ importData }) => ({
        url: 'playbooks/import/validate',
        method: 'post',
        body: {
          data: {
            type: 'playbook-import',
            attributes: { import_data: importData },
          },
        },
      }),
      transformErrorResponse: mapAtlasErrorToCodedIfClientError,
      invalidatesTags: [PLAYBOOKS_LIST_TAG],
    }),

    // ===== Questions / Question groups =====

    getQuestion: build.query<ReturnType<typeof mapQuestionResponse>, string>({
      query: (id) => ({ url: `questions/${id}` }),
      transformResponse: (response: QuestionResponsePayload) =>
        mapQuestionResponse(response.data),
      transformErrorResponse: mapAtlasError,
      providesTags: (_result, _error, id) => [
        { type: Tags.Saved_Questions, id },
      ],
    }),

    getQuestions: build.query<
      ReturnType<typeof mapQuestionResponse>[],
      { pageSize?: number; pageLimit?: number; search?: string }
    >({
      query: ({ pageSize = 0, pageLimit = 0, search = '' } = {}) => ({
        url: 'questions',
        params: {
          'page[offset]': pageSize,
          'page[limit]': pageLimit,
          search,
        },
      }),
      transformResponse: (response: QuestionsResponsePayload) =>
        response.data.map(mapQuestionResponse),
      transformErrorResponse: mapAtlasError,
      providesTags: [QUESTIONS_LIST_TAG],
    }),

    createQuestion: build.mutation<
      ReturnType<typeof mapQuestionResponse>,
      {
        text: string;
        scope: 'document_handler' | 'document_version' | 'all_documents';
      }
    >({
      query: (question) => ({
        url: 'questions',
        method: 'post',
        body: { data: { type: 'questions', attributes: { ...question } } },
      }),
      transformResponse: (response: QuestionResponsePayload) =>
        mapQuestionResponse(response.data),
      transformErrorResponse: mapAtlasError,
      invalidatesTags: (_result, error) => (error ? [] : [QUESTIONS_LIST_TAG]),
    }),

    updateQuestion: build.mutation<
      ReturnType<typeof mapQuestionResponse>,
      {
        id: string;
        pinnedAt?: Date | null;
        text?: string;
        name?: string;
        questionGroupIds?: string[];
      }
    >({
      query: ({ id, ...question }) => {
        const attributes: {
          pinned_at?: string | null;
          text?: string;
          name?: string;
          question_group_ids?: string[];
        } = {};

        Object.entries(question).forEach(([key, value]) => {
          if (key === 'pinnedAt') {
            attributes.pinned_at = value
              ? (value as Date).toISOString().replace('Z', '')
              : null;
          }
          if (key === 'text' && value) {
            attributes.text = value as string;
          }
          if (key === 'name' && value) {
            attributes.name = value as string;
          }
          if (key === 'questionGroupIds' && value) {
            attributes.question_group_ids = value as string[];
          }
        });

        return {
          url: `questions/${id}`,
          method: 'patch',
          body: { data: { type: 'questions', attributes } },
        };
      },
      transformResponse: (response: QuestionResponsePayload) =>
        mapQuestionResponse(response.data),
      transformErrorResponse: mapAtlasError,
      invalidatesTags: (_result, error) => (error ? [] : [QUESTIONS_LIST_TAG]),
    }),

    deleteQuestion: build.mutation<unknown, { id: string }>({
      query: ({ id }) => ({ url: `questions/${id}`, method: 'delete' }),
      transformErrorResponse: mapAtlasError,
      invalidatesTags: (_result, error) => (error ? [] : [QUESTIONS_LIST_TAG]),
    }),

    resolveQuestions: build.query<
      ReturnType<typeof mapQuestionResponse>[],
      { ids: string[] }
    >({
      queryFn: async ({ ids }, _api, _extra, fetchWithBQ) => {
        const results = await Promise.allSettled(
          ids.map((id) => fetchWithBQ({ url: `questions/${id}` })),
        );

        const questions = results
          .filter(
            (result) => result.status === 'fulfilled' && !result.value.error,
          )
          .map(
            (result) =>
              (
                (result as PromiseFulfilledResult<{ data?: unknown }>).value
                  .data as QuestionResponsePayload
              ).data,
          );

        return { data: questions.map(mapQuestionResponse) };
      },
    }),

    getQuestionGroups: build.query<
      ReturnType<typeof mapQuestionGroupResponse>[],
      {
        pageSize?: number;
        pageLimit?: number;
        search?: string | null;
        visibility?: 'PRIVATE' | 'SHARED' | null;
      }
    >({
      query: ({
        pageSize = 0,
        pageLimit = 0,
        search = null,
        visibility = null,
      } = {}) => ({
        url: 'question-groups',
        params: {
          'page[offset]': pageSize,
          'page[limit]': pageLimit,
          search,
          visibility,
        },
      }),
      transformResponse: (response: QuestionGroupsResponsePayload) =>
        response.data.map(mapQuestionGroupResponse),
      transformErrorResponse: mapAtlasError,
      providesTags: [QUESTION_GROUPS_LIST_TAG],
    }),

    createQuestionGroup: build.mutation<
      ReturnType<typeof mapQuestionGroupResponse>,
      {
        name: string;
        questionIds: string[];
        visibility: 'OPEN' | 'PRIVATE' | 'SHARED';
        userIds: number[];
        departmentIds: number[];
        description?: string;
      }
    >({
      query: (questionGroup) => ({
        url: 'question-groups',
        method: 'post',
        body: {
          data: {
            type: 'question_groups',
            attributes: {
              name: questionGroup.name,
              description: questionGroup.description,
              question_ids: questionGroup.questionIds,
              visibility: questionGroup.visibility,
              user_ids: questionGroup.userIds,
              department_ids: questionGroup.departmentIds,
            },
          },
        },
      }),
      transformResponse: (response: QuestionGroupResponsePayload) =>
        mapQuestionGroupResponse(response.data),
      transformErrorResponse: mapAtlasError,
      invalidatesTags: (_result, error) =>
        error ? [] : [QUESTION_GROUPS_LIST_TAG],
    }),

    updateQuestionGroup: build.mutation<
      ReturnType<typeof mapQuestionGroupResponse>,
      {
        id: string;
        name?: string;
        questionIds?: string[];
        pinnedAt?: Date | null;
        description?: string;
        userIds?: number[];
        departmentIds?: number[];
        visibility?: 'PRIVATE' | 'OPEN' | 'SHARED';
      }
    >({
      query: ({ id, ...questionGroup }) => {
        const attributes: {
          pinned_at?: string | null;
          description?: string;
          name?: string;
          question_ids?: string[];
          user_ids?: number[];
          department_ids?: number[];
          visibility?: 'PRIVATE' | 'OPEN' | 'SHARED';
        } = {};

        Object.entries(questionGroup).forEach(([key, value]) => {
          if (key === 'pinnedAt') {
            attributes.pinned_at = value
              ? (value as Date).toISOString().replace('Z', '')
              : null;
          }
          if (key === 'description' && value) {
            attributes.description = value as string;
          }
          if (key === 'name' && value) {
            attributes.name = value as string;
          }
          if (key === 'questionIds' && value) {
            attributes.question_ids = value as string[];
          }
          if (key === 'userIds' && value) {
            attributes.user_ids = (
              questionGroup.visibility === 'SHARED' ? value : []
            ) as number[];
          }
          if (key === 'departmentIds' && value) {
            attributes.department_ids = (
              questionGroup.visibility === 'SHARED' ? value : []
            ) as number[];
          }
          if (key === 'visibility' && value) {
            attributes.visibility = value as 'PRIVATE' | 'OPEN' | 'SHARED';
          }
        });

        return {
          url: `question-groups/${id}`,
          method: 'patch',
          body: { data: { type: 'question_groups', attributes } },
        };
      },
      transformResponse: (response: QuestionGroupResponsePayload) =>
        mapQuestionGroupResponse(response.data),
      transformErrorResponse: mapAtlasError,
      invalidatesTags: (_result, error) =>
        error ? [] : [QUESTION_GROUPS_LIST_TAG],
    }),

    getQuestionGroupQuestions: build.query<
      ReturnType<typeof mapQuestionResponse>[],
      { id: string }
    >({
      query: ({ id }) => ({ url: `question-groups/${id}/questions` }),
      transformResponse: (response: QuestionsResponsePayload) =>
        response.data.map(mapQuestionResponse),
      transformErrorResponse: mapAtlasError,
    }),

    resolveQuestionGroups: build.query<
      ReturnType<typeof mapQuestionGroupResponse>[],
      { ids: string[] }
    >({
      queryFn: async ({ ids }, _api, _extra, fetchWithBQ) => {
        const results = await Promise.all(
          ids.map((id) => fetchWithBQ({ url: `question-groups/${id}` })),
        );
        const errorResult = results.find((result) => result.error);
        if (errorResult) {
          return { error: errorResult.error };
        }
        return {
          data: results.map((result) =>
            mapQuestionGroupResponse(
              (result.data as QuestionGroupResponsePayload).data,
            ),
          ),
        };
      },
    }),

    deleteQuestionGroup: build.mutation<unknown, { id: string }>({
      query: ({ id }) => ({ url: `question-groups/${id}`, method: 'delete' }),
      transformErrorResponse: mapAtlasError,
      invalidatesTags: (_result, error) =>
        error ? [] : [QUESTION_GROUPS_LIST_TAG],
    }),

    // ===== Playbook insights =====

    getPlaybookInsights: build.query<PlaybookInsights, PlaybookInsightsArg>({
      query: (arg) => ({
        url: '/playbooks/analytics',
        method: 'post',
        body: mapPlaybookInsightsParams(arg),
      }),
      transformResponse: (response: PlaybookUsageAnalyticsResponse) =>
        mapPlaybookInsightsResponse(response),
    }),

    getPlaybookList: build.query<
      { rows: PlaybookTableRow[]; totalCount: number },
      PlaybookListArg
    >({
      query: (arg) => ({
        url: '/playbooks/list',
        params: mapPlaybookListParams(arg),
      }),
      transformResponse: (response: ListPlaybooksWithAnalyticsResponse) =>
        mapPlaybookListResponse(response),
      providesTags: [PLAYBOOKS_LIST_TAG],
    }),

    /** Sparse list for pickers / trees — request only the `fields` you need. */
    getPlaybookListSummaries: build.query<
      { items: PlaybookListSummary[]; totalCount: number },
      PlaybookListArg
    >({
      query: (arg) => ({
        url: '/playbooks/list',
        params: mapPlaybookListParams(arg),
      }),
      transformResponse: (response: ListPlaybooksWithAnalyticsResponse) =>
        mapPlaybookListSummaries(response),
      providesTags: [PLAYBOOKS_LIST_TAG],
    }),
  }),
});
